/* eslint-disable no-unused-vars */
import * as React from "react";
import { useSelector } from "react-redux";
import lodashGet from "lodash/get";

import jsPDF from "jspdf";
// eslint-disable-next-line

import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";

import ActionPrint from "@material-ui/icons/Print";
import ExcelIcon from "mdi-material-ui/FileExcel";
import CircularProgress from "@material-ui/core/CircularProgress";

import { Button, BackButton, translate } from "jazasoft";

import MuiTable from "../../../components/MuiTable";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSetting, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity, OState, RollType } from "../../../utils/types";
import { isEmpty, toFixed } from "../../../utils/helpers";

import useTenant from "../../../hooks/useTenant";
import { getCutOrderRows } from "../../cutplan/cutPlanHelper";
import { colorLightGreen } from "../../../utils/colors";
import { addTable, setDefault, totalPagesExp } from "../../../helpers/pdfHelper";
import { convertTableToCell } from "../../../utils/excelHelper";

const useStyles = makeStyles({
  content: {
    margin: "1.5em"
  },
  mrTop: {
    marginTop: "1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  margin: {
    margin: "1.5em"
  },
  form: {
    padding: "1.5em",
    paddingTop: 0
  },
  settings: {
    margin: "1em 1em 0 1em"
  }
});

const RollUtilizationReport = props => {
  const classes = useStyles();
  const id = props.match?.params?.id;

  const [selectedKey, setSelectedKey] = React.useState(null);
  const [printing, setPrinting] = React.useState(false);

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const tenant = useTenant();
  const { allUnitList } = useUnit();

  const messages = useSelector(state => state?.i18n?.messages || {});

  const [download, { loading: downloading }] = useMutation({ rawResponse: true });

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" } });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList)) return [];
    let rows = getCutOrderRows(order, productParts);
    rows = rows.map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
    return rows;
  }, [order, productParts]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      // { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }
    return cols;
  }, [flowInfoRows, allUnitList]);

  useDeepEffect(() => {
    if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  const defaultExpanded = flowInfoRows.filter(fi => isEmpty(fi.parentKey)).length === 1;

  const selectedRow = flowInfoRows.find(fi => fi.key === selectedKey);
  const cutOrder = flowInfoRows.find(fi => fi.key === selectedKey)?.cutOrder || {};
  const { data: rollList = [] } = useQuery(
    !isEmpty(cutOrder) && {
      url: `rolls`,
      params: {
        search: `cutOrderFlow.id==${cutOrder?.id};(hasChild=='NULL',hasChild=='FALSE');(active=='TRUE',active=='FALSE',active=='NULL')`,
        size: 100000
      }
    }
  );
  const { data: layList = [] } = useQuery(!isEmpty(cutOrder) && { url: `lays`, params: { search: `orderFlow.id==${cutOrder?.id}` } });
  const { data: layRowData = [] } = useQuery(
    !isEmpty(cutOrder) && {
      url: "lay-rows",
      params: { search: `lay.orderFlow.id==${cutOrder?.id};completed=='TRUE';(skipped=='NULL',skipped=='FALSE')` }
    }
  );

  const hasWeight = Array.isArray(rollList) && rollList.some(e => !isEmpty(e.weight) && e.weight > 0);

  const columns = useDeepMemo(() => {
    let columnList = [];
    if (hasWeight) {
      columnList = [
        { dataKey: "serialNo", title: "SL. No", align: "center", type: "string" },
        { dataKey: "rollNo", title: "F. Roll No", align: "left", type: "string" },
        { dataKey: "supplierRollNo", title: "S. Roll No", align: "left", type: "string" },
        { dataKey: "invStatus", title: "Inv. Status", align: "left", type: "string" },
        { dataKey: "weight", title: "Length \n(kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "width", title: "Width \n(cm)" },
        { dataKey: "returned", title: "Returned \n(kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "blanketQty", title: "Testing \nLoss (kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "lays", title: "Lay", type: "string", cellStyle: { wrapText: true } },
        { dataKey: "consumedInLay", title: "Consumed \nin Lay", type: "string", cellStyle: { wrapText: true, hAlign: "right" } },
        { dataKey: "usedInLay", title: "Used in Lay \n(kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "grossEndBits", title: "Gross \nEnd Bits (kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "remnantUsedInPanel", title: "Panel Recut -\nRem. (kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "netEndBits", title: "Net \nEnd Bits (kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "rollUsedInPanel", title: "Panel Recut -\n Roll (kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "damaged", title: "Damaged \n(kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "missing", title: "Missing \n(kg)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        {
          dataKey: "unused",
          title: "Unused \n(kg)",
          render: value =>
            isEmpty(value) ? (
              "-"
            ) : (
              <span style={{ padding: "5px 10px", backgroundColor: value > 0 ? "#BB2124" : null, color: value > 0 ? "#fff" : "black" }}>
                {toFixed(value, 2)}
              </span>
            )
        }
        // { dataKey: "shortageExcess", title: "Shortage/\nExcess (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) }
      ];
    } else {
      columnList = [
        { dataKey: "serialNo", title: "SL. No", align: "center", type: "string" },
        { dataKey: "rollNo", title: "F. Roll No", align: "left", type: "string" },
        { dataKey: "supplierRollNo", title: "S. Roll No", align: "left", type: "string" },
        { dataKey: "invStatus", title: "Inv. Status", align: "left", type: "string" },
        { dataKey: "length", title: "Length \n(m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "width", title: "Width \n(cm)" },
        { dataKey: "returned", title: "Returned \n(m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "blanketQty", title: "Testing \nLoss (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "lays", title: "Lay", type: "string", cellStyle: { wrapText: true } },
        { dataKey: "consumedInLay", title: "Consumed \nin Lay", type: "string", cellStyle: { wrapText: true, hAlign: "right" } },
        { dataKey: "usedInLay", title: "Used in Lay \n(m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "grossEndBits", title: "Gross \nEnd Bits (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "remnantUsedInPanel", title: "Panel Recut -\nRem. (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "netEndBits", title: "Net \nEnd Bits (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "rollUsedInPanel", title: "Panel Recut -\n Roll (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        { dataKey: "damaged", title: "Damaged \n(m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) },
        {
          dataKey: "missing",
          title: "Missing \n(m)",
          render: value =>
            isEmpty(value) ? (
              "-"
            ) : (
              <span style={{ padding: "5px 10px", backgroundColor: value > 0 ? "#BB2124" : null, color: value > 0 ? "#fff" : "black" }}>
                {toFixed(value, 2)}
              </span>
            )
        },
        {
          dataKey: "unused",
          title: "Unused \n(m)",
          render: value =>
            isEmpty(value) ? (
              "-"
            ) : (
              <span style={{ padding: "5px 10px", backgroundColor: value > 0 ? "#BB2124" : null, color: value > 0 ? "#fff" : "black" }}>
                {toFixed(value, 2)}
              </span>
            )
        },
        { dataKey: "shortageExcess", title: "Shortage/\nExcess (m)", render: value => (isEmpty(value) ? "-" : toFixed(value, 2)) }
      ];
    }

    return columnList.map(col => ({ ...col, align: col.align || "right" }));
  }, [hasWeight]);

  const rows = useDeepMemo(() => {
    let rowList = [];
    if (hasWeight) {
      rowList = rollList
        .sort((a, b) => (a.rollNo < b.rollNo ? -1 : 1))
        .map(roll => ({
          ...roll,
          blanketQty: roll.blanketWeight || 0,
          grossEndBits: roll.grossEndBitsWeight || 0,
          netEndBits: roll.netEndBitsWeight || 0,
          usedInPanel: roll.usedInPanelWeight || 0
        }))
        .map((roll, idx) => {
          const rowList = layRowData
            .filter(row => row?.rollInfoId === roll?.id)
            .map(row => {
              const marker = cutOrder.markerList?.find(m => m.id === row.markerId);
              const lay = layList.find(e => e?.id === row.layId);
              const usedInLay = (row?.actualPly || 0) * (marker?.layLength || 0);
              return { layNo: lay?.layNo, lay: `Lay ${lay?.layNo}`, used: toFixed(usedInLay), usedInLay };
            })
            .filter(e => e.usedInLay > 0)
            .sort((a, b) => a.layNo - b.layNo);
          const lays = isEmpty(rowList) ? "-" : rowList.map(e => e.lay).join("\r\n");
          const consumedInLay = isEmpty(rowList) ? "-" : rowList.map(e => e.used).join("\r\n");
          const unused = roll.netEndBits > 0 || roll.returnStatus === "Returned" || roll.returnStatus === "Missed" || roll.returnStatus === "Confirmed" ? 0 : roll.availableWeight;

          return {
            ...roll,
            serialNo: idx + 1,
            length: roll.length + roll.blanketQty,
            weight: roll.weight + (roll.blanketWeight || 0),
            lays,
            usedInLay: roll.usedInLayWeight,
            consumedInLay,
            returned: roll.returnStatus === "Returned" || roll.returnStatus === "Confirmed" ? roll.weight : 0,
            netEndBits: roll.netEndBitsWeight,
            unused,
            remnantUsedInPanel: Math.max(0, roll.grossEndBitsWeight - roll.netEndBitsWeight),
            rollUsedInPanel: Math.max(0, roll.usedInPanelWeight - (roll.grossEndBitsWeight - roll.netEndBitsWeight)),
            missing: (isEmpty(roll.returnStatus) ? roll.rollType === RollType.MISSING : roll.returnStatus === "Missed") ? roll.weight : 0
          };
        });
    } else {
      rowList = rollList
        .sort((a, b) => (a.rollNo < b.rollNo ? -1 : 1))
        .map(roll => ({
          ...roll,
          blanketQty: roll.blanketQty || 0,
          grossEndBits: roll.grossEndBits || 0,
          netEndBits: roll.netEndBits || 0,
          usedInPanel: roll.usedInPanel || 0
        }))
        .map((roll, idx) => {
          const rowList = layRowData
            .filter(row => row?.rollInfoId === roll?.id)
            .map(row => {
              const marker = cutOrder.markerList?.find(m => m.id === row.markerId);
              const lay = layList.find(e => e?.id === row.layId);
              const usedInLay = (row?.actualPly || 0) * (marker?.layLength || 0);
              return { layNo: lay?.layNo, lay: `Lay ${lay?.layNo}`, used: toFixed(usedInLay), usedInLay };
            })
            .filter(e => e.usedInLay > 0)
            .sort((a, b) => a.layNo - b.layNo);
          const lays = isEmpty(rowList) ? "-" : rowList.map(e => e.lay).join("\r\n");
          const consumedInLay = isEmpty(rowList) ? "-" : rowList.map(e => e.used).join("\r\n");
          const unused = roll.netEndBits > 0 || roll.returnStatus === "Returned" || roll.returnStatus === "Missed" || roll.returnStatus === "Confirmed" ? 0 : roll.available;

          return {
            ...roll,
            serialNo: idx + 1,
            length: roll.length + roll.blanketQty,
            lays,
            consumedInLay,
            returned: roll.returnStatus === "Returned" || roll.returnStatus === "Confirmed" ? roll.length : 0,
            netEndBits: roll.netEndBits,
            unused,
            remnantUsedInPanel: Math.max(0, roll.grossEndBits - roll.netEndBits),
            rollUsedInPanel: Math.max(0, roll.usedInPanel - (roll.grossEndBits - roll.netEndBits)),
            missing: (isEmpty(roll.returnStatus) ? roll.rollType === RollType.MISSING : roll.returnStatus === "Missed") ? roll.length : 0
          };
        });
    }

    return rowList;
  }, [rollList, layRowData, layList, hasWeight]);

  const onPrint = () => {
    setPrinting(true);

    var doc = new jsPDF("l", "mm", "a4");

    const unit = allUnitList.find(e => e.unitId === selectedRow?.unitId) || {};
    setDefault(doc, "Roll Utilization Report", tenant, null, unit);
    const suffix = `${order.customer.name}, ${order.poRef}`;
    doc.setFontSize(10);

    let startY = 30;

    ////// Print Order Info //////////////
    const flowColumns = flowInfoColumns.map(({ dataKey, title }) => ({
      dataKey,
      header: orderFields.includes(dataKey)
        ? lodashGet(messages, `resources.orders.fields.${dataKey}`, title)
        : lodashGet(messages, `resources.orders.fields.flowInfo.${dataKey}`, title)
    }));
    let autoTableOptions = {
      startY: startY + 3,
      body: [selectedRow],
      columns: flowColumns,
      halign: "center"
    };
    let options = {
      colWidthMap: { effPartName: 10, lotValue: 9, orderQty: 12, fpoQty: 10 }
    };
    doc.text("Cut Order", 7, startY);
    addTable(doc, autoTableOptions, options);
    startY = doc.autoTable.previous.finalY;

    ////// Print Roll Info //////////////
    const rolls = rows.map(row => ({
      ...row,
      ...columns.filter(c => c.type !== "string").reduce((acc, col) => ({ ...acc, [col.dataKey]: toFixed(row[col.dataKey]) }), {})
    }));
    const totalRow = columns
      .filter(c => c.type !== "string" && c.dataKey !== "width")
      .reduce(
        (acc, col) => ({
          ...acc,
          [col.dataKey]: toFixed(rows.reduce((acc, row) => acc + (row[col.dataKey] || 0), 0))
        }),
        { rollNo: "Total" }
      );

    autoTableOptions = {
      startY: startY + 10,
      body: [...rolls, totalRow],
      columns,
      halign: "center"
    };
    options = {};
    doc.text("Roll Utilization", 7, startY + 7);
    addTable(doc, autoTableOptions, options);

    doc.putTotalPages(totalPagesExp);
    let filename = `ROLL UTILIZATION REPORT - ${suffix}.pdf`;
    filename = filename.replace(/\//g, "_");
    doc.save(filename);

    setPrinting(false);
  };

  const onDownload = () => {
    const filename = "ROLL_UTILIZATION_REPORT.xlsx";

    const finalMaxColNo = Math.max(flowInfoColumns?.length, columns?.length);

    const flowColumns = flowInfoColumns.map(({ dataKey, title }) => ({
      dataKey,
      title: orderFields.includes(dataKey)
        ? lodashGet(messages, `resources.orders.fields.${dataKey}`, title)
        : lodashGet(messages, `resources.orders.fields.flowInfo.${dataKey}`, title)
    }));

    const cutOrderRows = [selectedRow];
    const rolls = rows.map(row => ({
      ...row,
      ...columns.filter(c => c.type !== "string").reduce((acc, col) => ({ ...acc, [col.dataKey]: toFixed(row[col.dataKey]) }), {})
    }));
    const totalRow = columns
      .filter(c => c.type !== "string" && c.dataKey !== "width")
      .reduce(
        (acc, col) => ({
          ...acc,
          [col.dataKey]: toFixed(rows.reduce((acc, row) => acc + (row[col.dataKey] || 0), 0))
        }),
        { rollNo: "Total", supplierRollNo: "", serialNo: "", lays: "", consumedInLay: "", invStatus: "" }
      );
    const rollRows = [...rolls, totalRow];

    const cutOrderCellList = convertTableToCell(flowColumns, cutOrderRows, 4, finalMaxColNo);
    const rollCellList = convertTableToCell(columns, rollRows, cutOrderRows.length + 6, finalMaxColNo);
    const unit = allUnitList?.find(e => e.unitId === selectedRow?.unitId) || {};
    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: unit?.displayName || unit?.name || tenant?.name || "",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 48
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Roll Utlization Report",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 36
      },
      { rowNo: 3, colNo: 1, colEnd: 2, value: "Cut Order", cellStyle: { hAlign: "left", bold: true, bgColor: "230,230,230" } },
      { rowNo: 3, colNo: 3, colEnd: finalMaxColNo, cellStyle: { bgColor: "230,230,230" } },
      ...cutOrderCellList,
      {
        rowNo: cutOrderRows.length + 2 + 3,
        colNo: 1,
        colEnd: 2,
        value: "Roll Utlization",
        cellStyle: { hAlign: "left", bold: true, bgColor: "230,230,230" }
      },
      { rowNo: cutOrderRows.length + 2 + 3, colNo: 3, colEnd: finalMaxColNo, cellStyle: { bgColor: "230,230,230" } },
      ...rollCellList
    ];

    const excelSheet = {
      sheetNo: 0,
      sheetName: "Roll Utilization Report",
      autoSizeColumn: false,
      excelData: { cellList: finalCellList }
    };

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      params: { filename },
      data: [excelSheet]
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          const fName = header ? header.match(/filename="(.+)"/)[1] : filename;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fName;
          link.click();
        }
      }
    });
  };

  return (
    <div>
      <div className={classes.content}>
        <MuiTable
          title="Cut Order"
          //   title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
          toolbar={true}
          searchable={true}
          pageable={true}
          searchKeys={["style", "color", "flowRef", "customerFlowRef", "fpo"]}
          idKey="key"
          parentIdKey="parentKey"
          columns={flowInfoColumns}
          rows={flowInfoRows}
          defaultExpanded={defaultExpanded}
          expandedColor={["#BFDDF8", "#7AB9F0"]}
          onRowClick={onRowClick}
          rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
          i18nKey="resources.grouping.fields.flowInfo"
        />

        {(!cutOrder || cutOrder.stateValue < OState.FABRIC_ALLOCATED.stateValue) && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Roll not allocated yet.</Typography>
          </Alert>
        )}

        {cutOrder.stateValue >= OState.FABRIC_ALLOCATED.stateValue && (
          <div style={{ marginTop: "1.5em" }}>
            <MuiTable
              title="Roll Utilization Report"
              toolbar={true}
              searchable={true}
              pageable={true}
              searchKeys={["rollNo", "supplierRollNo"]}
              columns={columns}
              rows={rows}
              parentIdKey="unknown"
            />

            <div style={{ marginTop: "1.5em", display: "flex", gap: "1em" }}>
              <Button label="Print" variant="contained" color="primary" disabled={printing} onClick={onPrint}>
                {printing ? <CircularProgress size={25} thickness={2} color="secondary" /> : <ActionPrint />}
              </Button>
              <Button
                label="Download"
                variant="outlined"
                disabled={downloading}
                onClick={onDownload}
                style={{ color: colorLightGreen.main, borderColor: colorLightGreen.main }}>
                {downloading ? <CircularProgress size={25} thickness={2} color="secondary" /> : <ExcelIcon />}
              </Button>
              <BackButton variant="outlined" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default translate(RollUtilizationReport);
